import { html } from 'lit';
import { DataTableConfig, ColumnDefinition, FilterDefinition, ActionDefinition } from '../../common/data-table/data-table.types.js';
import { UserRole } from '@cmms/models';

interface User {
  id: string;
  model: {
    user_id: string;
    company_id: string;
    name: string;
    email: string;
    role: UserRole;
    created_at: number;
    updated_at: number;
  };
  authData?: {
    lastSignInTime: string | null;
    disabled: boolean;
    emailVerified: boolean;
  };
}

export function createUsersTableConfig(
  users: User[],
  handlers: {
    onRowClick: (user: User) => void;
    onDelete: (user: User) => void;
    onAddUser: () => void;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
  }
): DataTableConfig<User> {

  const getRoleBadgeVariant = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN: return 'danger';
      case UserRole.MANAGER: return 'warning';
      case UserRole.TECHNICIAN: return 'primary';
      case UserRole.SUPERVISOR: return 'success';
      default: return 'neutral';
    }
  };

  const getRoleDisplayName = (role: UserRole): string => {
    switch (role) {
      case UserRole.ADMIN: return 'Admin';
      case UserRole.MANAGER: return 'Manager';
      case UserRole.TECHNICIAN: return 'Technician';
      case UserRole.SUPERVISOR: return 'Supervisor';
      default: return role;
    }
  };

  const columns: ColumnDefinition<User>[] = [
    {
      key: 'name',
      label: 'Name',
      type: 'custom',
      sortable: true,
      className: 'name-cell',
      renderer: (value: any, user: User) => html`
        <div class="user-info">
          
          <span>${user.model.name}</span>
        </div>
      `,
      width: '180px'
    },
    {
      key: 'email',
      label: 'Email',
      type: 'text',
      sortable: true,
      formatter: (value: any, user: User) => user.model.email,
      width: '200px'
    },
    {
      key: 'role',
      label: 'Role',
      type: 'custom',
      sortable: true,
      renderer: (value: any, user: User) => html`
        <sl-badge variant=${getRoleBadgeVariant(user.model.role)}>
          ${getRoleDisplayName(user.model.role)}
        </sl-badge>
      `,
      width: '100px'
    },
    {
      key: 'created_at',
      label: 'Created',
      type: 'date',
      sortable: true,
      formatter: (value: any, user: User) => new Date(user.model.created_at).toLocaleDateString(),
      width: '120px'
    },
    {
      key: 'lastSignInTime',
      label: 'Last Login',
      type: 'date',
      sortable: true,
      formatter: (value: any, user: User) => {
        if (!user.authData?.lastSignInTime) {
          return 'Never';
        }
        
        const date = new Date(user.authData.lastSignInTime);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      },
      width: '120px'
    }
  ];

  const filters: FilterDefinition[] = [
    {
      key: 'role',
      label: 'Role',
      type: 'select',
      options: [
        { value: '', label: 'All Roles' },
        { value: UserRole.ADMIN, label: 'Admin' },
        { value: UserRole.MANAGER, label: 'Manager' },
        { value: UserRole.TECHNICIAN, label: 'Technician' },
        { value: UserRole.SUPERVISOR, label: 'Supervisor' }
      ]
    }
  ];

  const rowActions: ActionDefinition[] = [
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: 'trash',
      onClick: (user: User) => handlers.onDelete(user)
    }
  ];

  const headerActions: ActionDefinition[] = [
    {
      key: 'add',
      label: 'Add User',
      variant: 'primary',
      icon: 'person-plus',
      onClick: () => handlers.onAddUser()
    }
  ];

  return {
    data: users,
    columns,
    filters,
    rowActions,
    headerActions,
    searchPlaceholder: 'Search users...',
    emptyState: {
      icon: 'person-x',
      title: 'No users found',
      message: 'Get started by adding your first user.'
    },
    defaultSort: { key: 'name', direction: 'asc' },
    loading: false,
    error: null,
    clickableRows: true,
    onRowClick: handlers.onRowClick,
    onFilterChange: handlers.onFilterChange,
    onSort: handlers.onSort
  };
}
